import { ModuleKey } from '../enums/module-key.enum';

/** Platform-level permissions (Super Admin only). */
export const PlatformPermission = {
  AgenciesRead: 'platform:agencies:read',
  AgenciesWrite: 'platform:agencies:write',
  ModulesToggle: 'platform:modules:toggle',
  /**
   * Read mailer import runs in the Super Admin panel (PAC-73).
   *
   * ⚠ Distinct from the agency-facing `mailers:read` **module** permission that
   * PAC-61's drawer uses. That one is a page inside an agency's own app and is
   * filtered by the agency's module entitlements; this is a platform capability
   * and bypasses that filter entirely (see `resolvePermissionSet`). Granting
   * one never implies the other.
   */
  MailersRead: 'platform:mailers:read',
  /** Upload an RTP file and commit the mailers it produces (PAC-73). */
  MailersWrite: 'platform:mailers:write',
  /**
   * Search the cross-agency user directory in the Super Admin panel (PAC-70).
   *
   * The only read that returns users from *every* tenant. Separate from
   * `UsersImpersonate` so "can find a person" and "can act as a person" stay
   * independently grantable — an operator who triages support tickets needs
   * the first without the second.
   */
  UsersRead: 'platform:users:read',
  /**
   * Mint a session as another user, without their password (PAC-70).
   *
   * The strongest capability on the platform: it hands the holder every
   * permission the target has, inside the target's own tenant. Deliberately a
   * separate string rather than something `isPlatformAdmin` implies, so that
   * "can administer agencies" and "can act as a person" stay independently
   * grantable — and so revoking it does not require demoting an admin.
   *
   * What bounds it is the target, not the caller: `AuthService.impersonate`
   * refuses another platform admin, so this can never be used to climb sideways
   * into a peer's authority, only downwards into a tenant's.
   */
  UsersImpersonate: 'platform:users:impersonate',
  /**
   * Read the in-app bug-report queue.
   *
   * ⚠ There is no agency-facing counterpart, and adding one is a decision
   * rather than an omission: reports arrive from every tenant, name the
   * screens and data the reporter was looking at, and are triaged by whoever
   * operates the platform. **Filing** a report needs no permission at all —
   * `POST /bug-reports` declares none, so every authenticated user can reach
   * it whatever their modules. Reading them is what is restricted.
   */
  BugsRead: 'platform:bugs:read',
  /** Set a report's triage status and internal notes. */
  BugsWrite: 'platform:bugs:write',
} as const;

/** Agency administration permissions (Agency Owner manages users/branches). */
export const AgencyPermission = {
  /** View system role templates when assigning roles to users. */
  RolesRead: 'agency:roles:read',
  /** Edit a role's permission set. */
  RolesWrite: 'agency:roles:write',
  UsersRead: 'agency:users:read',
  UsersWrite: 'agency:users:write',
  UsersPermissions: 'agency:users:permissions',
  BranchesRead: 'agency:branches:read',
  BranchesWrite: 'agency:branches:write',
  /**
   * See field-level edit history on the activity timeline (PAC-65 #9).
   *
   * Editing a recorded quote or a booked policy stays open to everyone who can
   * write one — the product owner was explicit that a lock is the wrong answer
   * to the transparency concern. This is the log that answers it instead, and
   * producers must not see it.
   *
   * **Why it lives in the `agency:` namespace rather than as a third verb on a
   * module.** It is a capability, not a page, and the machinery only tolerates
   * `read`/`write` as a module's second segment:
   * - `permission-model.spec.ts` asserts every page permission is `{m}:read|write`;
   * - `RolesService.updateLevels` preserves only `agency:`/`platform:` strings
   *   when an owner edits the role matrix, so a `leads:changelog:read` would be
   *   **silently dropped** the first time someone touched that screen;
   * - `resolvePermissionSet`'s enabled-module filter would read `changelogs:` as
   *   a module key and drop a standalone string entirely.
   *
   * ⚠ `grantsAllEnabledModules` expands only `{m}:read`/`{m}:write`, so this
   * reaches the Agency Owner through `DEFAULT_ROLE_TEMPLATES` — not through
   * that flag — and an already-seeded agency needs `npm run api:sync:roles`.
   */
  ChangeLogsRead: 'agency:changelogs:read',
  /**
   * White-labelling (§ the white-label epic). Three pairs rather than one
   * because they carry three very different blast radii, and an owner may well
   * want to delegate the harmless one without the other two:
   *
   * | Pair | Worst case if misused |
   * |---|---|
   * | `branding` | A wrong logo. Cosmetic, instantly reversible. |
   * | `domains`  | The agency's app is unreachable at its own hostname. |
   * | `email`    | Every invite and share link stops arriving. |
   *
   * Same `agency:` namespace requirement as {@link ChangeLogsRead} above, for
   * the same three reasons written up there — a bare `branding:read` would be
   * read as a *module* key by `resolvePermissionSet`'s enabled-module filter
   * and dropped, and `RolesService.updateLevels` would discard it the first
   * time an owner touched the role matrix.
   *
   * ⚠ Also like {@link ChangeLogsRead}, these reach the Agency Owner only
   * through `DEFAULT_ROLE_TEMPLATES`, so an already-seeded agency has none of
   * them until someone runs `npm run api:sync:roles`. They shipped without that
   * and every existing owner was locked out of the settings pages (PAC-133).
   */
  BrandingRead: 'agency:branding:read',
  BrandingWrite: 'agency:branding:write',
  DomainsRead: 'agency:domains:read',
  DomainsWrite: 'agency:domains:write',
  EmailRead: 'agency:email:read',
  EmailWrite: 'agency:email:write',
  /**
   * Carrier appointments — the agency code each carrier issued this agency
   * (PAC-93). A fourth pair for the same reason the three above are three: its
   * blast radius is its own. A wrong code files this agency's mailer prospects
   * under another tenant, or under none — the appointment is what PAC-71 routes
   * a vendor file's `agencyid` column by.
   *
   * ⚠ **Not `agency:carriers:*`.** `carrier.schema.ts` already reserves that
   * for agency-owner CRUD over the carrier *catalog*, which is a different
   * thing: the catalog says which insurers exist, an appointment says which of
   * them appointed you and under what code. Two capabilities must not share a
   * string.
   *
   * Same `agency:` namespace requirement as {@link ChangeLogsRead} above, for
   * the same three reasons written up there.
   */
  CarrierAppointmentsRead: 'agency:carrier_appointments:read',
  CarrierAppointmentsWrite: 'agency:carrier_appointments:write',
  /**
   * Hand a person's open work — tickets, clients, leads, audits, rotation
   * slots, share links — to a named colleague (PAC-136). The departing-
   * employee case: what is still to be done moves, what was done stays
   * credited to whoever did it.
   *
   * Its own permission rather than `agency:users:write`: the product owner
   * wants it delegable on its own, and reassigning a whole book of clients is
   * a different blast radius from inviting someone. It is still granted per
   * role, but only an **agency-wide** role can use it — a branch- or own-scoped
   * holder is refused by the service (product owner, 2026-10-05) — and work
   * never crosses from one branch to another.
   *
   * Same `agency:` namespace requirement as {@link ChangeLogsRead} above, and
   * the same ⚠: it reaches existing owners only through the role-template
   * migration that shipped with it.
   */
  WorkTransfer: 'agency:work_transfer:write',
  /**
   * Agency settings — how the agency's working day runs: its time zone and the
   * hour everyone is set Away (`/settings/agency`, PAC-149).
   *
   * PAC-141 put the time zone behind the branding pair, which was defensible
   * for one rarely-touched field. With the end-of-day hour beside it, the page
   * now does something to every user every night, and an owner delegating
   * "branding" on the role matrix would unknowingly hand over the clock that
   * puts the office Away. Its blast radius is its own — the rule the four
   * pairs above follow. PAC-148's working-days calendar and agency holidays
   * belong here too.
   *
   * Same `agency:` namespace requirement as {@link ChangeLogsRead} above, for
   * the same three reasons written up there.
   *
   * ⚠ Existing owners received this pair from the
   * `agency_settings_permission_grant` migration, not from
   * `DEFAULT_ROLE_TEMPLATES` — the template only reaches an agency when it is
   * created. Without that migration every owner would have lost the page the
   * moment it shipped (the PAC-133 lock-out), and onboarding a new agency
   * would have failed on a key with no catalog row.
   */
  SettingsRead: 'agency:settings:read',
  SettingsWrite: 'agency:settings:write',
} as const;

export type ModuleAction = 'read' | 'write';

export function modulePermission(
  module: ModuleKey | string,
  action: ModuleAction,
): string {
  return `${module}:${action}`;
}

export function permissionsForModule(
  module: ModuleKey | string,
  actions: ModuleAction[] = ['read', 'write'],
): string[] {
  return actions.map((action) => modulePermission(module, action));
}

export const ALL_PLATFORM_PERMISSIONS = Object.values(PlatformPermission);
export const ALL_AGENCY_ADMIN_PERMISSIONS = Object.values(AgencyPermission);

export function moduleFromPermission(permission: string): string | null {
  if (permission.startsWith('platform:') || permission.startsWith('agency:')) {
    return null;
  }
  const [module] = permission.split(':');
  return module ?? null;
}

export function isPlatformPermission(permission: string): boolean {
  return permission.startsWith('platform:');
}

export function isAgencyAdminPermission(permission: string): boolean {
  return permission.startsWith('agency:');
}
